import { exportColorAsPng, isDark, toHex, toHsl, toHsv, toRgb, type RGBA } from '../lib/color';
import { toast } from './toast';
import { t } from '../lib/i18n';

interface DisplayRow {
  label: string;
  getValue: (c: RGBA) => string;
}

const ROWS: DisplayRow[] = [
  { label: 'HEX', getValue: (c) => toHex(c) },
  { label: 'RGB', getValue: (c) => toRgb(c) },
  { label: 'HSL', getValue: (c) => toHsl(c) },
  {
    label: 'HSV',
    getValue: (c) => {
      const { h, s, v } = toHsv(c);
      return `hsv(${h}, ${s}%, ${v}%)`;
    }
  },
  {
    label: 'A',
    getValue: (c) => `${Math.round(c.a * 100)}%`
  }
];

const EXPORT_TEMPLATES: { label: string; build: (c: RGBA) => string }[] = [
  { label: 'CSS', build: (c) => `--color-primary: ${toHex(c)};` },
  {
    label: 'Tailwind',
    build: (c) => `'primary': '${toHex(c).toLowerCase()}',`
  },
  { label: 'SCSS', build: (c) => `$color-primary: ${toHex(c)};` }
];

async function copy(text: string, label: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast(t('Copied {value}', { value: label }));
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      toast(t('Copied {value}', { value: label }));
    } catch {
      toast(t('Copy failed, please copy manually'));
    } finally {
      ta.remove();
    }
  }
}

export interface BlockRef {
  root: HTMLElement;
  update: (c: RGBA) => void;
  updateTexts: () => void;
  getHexInput: () => HTMLInputElement;
  onHexCommit: (fn: (hex: string) => void) => void;
  onShare: (fn: () => void) => void;
}

export function createBlock(initial: RGBA): BlockRef {
  const root = document.createElement('section');
  root.className = 'block';

  const swatch = document.createElement('div');
  swatch.className = 'block__swatch';

  const info = document.createElement('div');
  info.className = 'block__info';

  const hexInput = document.createElement('input');
  hexInput.className = 'block__hex-input';
  hexInput.spellcheck = false;
  hexInput.setAttribute('aria-label', t('HEX Input'));

  const rows = document.createElement('div');
  rows.className = 'block__rows';
  const rowValueEls: HTMLSpanElement[] = [];
  for (const row of ROWS) {
    const line = document.createElement('div');
    line.className = 'block__row';

    const label = document.createElement('span');
    label.className = 'block__row-label';
    label.textContent = row.label;

    const value = document.createElement('span');
    value.className = 'block__row-value';
    value.textContent = row.getValue(initial);
    rowValueEls.push(value);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'block__row-copy';
    btn.title = t('Copy');
    btn.setAttribute('aria-label', t('Copy {label}', { label: row.label }));
    btn.textContent = '📋';

    line.append(label, value, btn);
    rows.appendChild(line);
  }

  const exportRow = document.createElement('div');
  exportRow.className = 'block__export';
  const exportLabel = document.createElement('span');
  exportLabel.className = 'block__export-label';
  exportLabel.textContent = t('Export');
  exportRow.appendChild(exportLabel);
  const exportButtons: HTMLButtonElement[] = [];
  for (const tmpl of EXPORT_TEMPLATES) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn--ghost';
    btn.textContent = tmpl.label;
    exportRow.appendChild(btn);
    exportButtons.push(btn);
  }

  const shareBtn = document.createElement('button');
  shareBtn.type = 'button';
  shareBtn.className = 'btn btn--ghost';
  shareBtn.textContent = 'Share';
  shareBtn.title = 'Copy share URL';
  exportRow.appendChild(shareBtn);

  const pngBtn = document.createElement('button');
  pngBtn.type = 'button';
  pngBtn.className = 'btn btn--ghost';
  pngBtn.textContent = 'PNG';
  pngBtn.title = 'Export as PNG';
  exportRow.appendChild(pngBtn);

  info.append(hexInput, rows, exportRow);
  root.append(swatch, info);

  let hexCommit: ((hex: string) => void) | null = null;
  let shareFn: (() => void) | null = null;

  shareBtn.addEventListener('click', () => {
    if (shareFn) shareFn();
  });

  let currentColor: RGBA = initial;
  pngBtn.addEventListener('click', () => {
    exportColorAsPng(currentColor);
  });

  hexInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      hexInput.blur();
    }
  });
  hexInput.addEventListener('blur', () => {
    if (hexCommit) hexCommit(hexInput.value);
  });

  function update(c: RGBA): void {
    currentColor = c;
    swatch.style.background = c.a < 1 ? rgbaCss(c) : toHex(c);
    swatch.classList.toggle('block__swatch--light', !isDark(c));
    hexInput.value = toHex(c);
    hexInput.placeholder = toHex(c);
    ROWS.forEach((row, i) => {
      rowValueEls[i].textContent = row.getValue(c);
    });
    // 复制按钮
    Array.from(rows.children).forEach((line, i) => {
      const btn = line.querySelector('.block__row-copy') as HTMLButtonElement;
      const v = ROWS[i].getValue(c);
      btn.onclick = () => copy(v, v);
    });
    // 导出按钮
    exportButtons.forEach((btn, i) => {
      btn.onclick = () => copy(EXPORT_TEMPLATES[i].build(c), EXPORT_TEMPLATES[i].label);
    });
  }

  function onHexCommit(fn: (hex: string) => void): void {
    hexCommit = fn;
  }

  function onShare(fn: () => void): void {
    shareFn = fn;
  }

  function getHexInput(): HTMLInputElement {
    return hexInput;
  }

  function updateTexts(): void {
    hexInput.setAttribute('aria-label', t('HEX Input'));
    // 复制按钮标题
    Array.from(rows.children).forEach((line, i) => {
      const btn = line.querySelector('.block__row-copy') as HTMLButtonElement;
      btn.title = t('Copy');
      btn.setAttribute('aria-label', t('Copy {label}', { label: ROWS[i].label }));
    });
    // 导出标签
    exportLabel.textContent = t('Export');
  }

  // 首次同步
  update(initial);

  return { root, update, updateTexts, getHexInput, onHexCommit, onShare };
}

function rgbaCss(c: RGBA): string {
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a.toFixed(3)})`;
}
